import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

const base = {
  minHeight: '100vh',
  background: 'radial-gradient(circle at 20% 10%, rgba(147,51,234,.16), transparent 38%), #0b0712',
  color: '#f7f2ff',
  fontFamily: 'Inter, system-ui, sans-serif',
  padding: '32px 20px 72px'
}
const card = {
  maxWidth: 920,
  margin: '0 auto',
  padding: 'clamp(24px,4vw,48px)',
  border: '1px solid rgba(216,180,254,.2)',
  borderRadius: 28,
  background: 'rgba(255,255,255,.055)',
  backdropFilter: 'blur(18px)',
  boxShadow: '0 24px 80px rgba(0,0,0,.28)'
}
const link = { color: '#d8b4fe', textDecoration: 'none', fontWeight: 700 }

function useSeo(title, description, canonicalPath, robots = 'index, follow') {
  useEffect(() => {
    document.title = title
    const upsert = (selector, attr, value) => {
      let el = document.head.querySelector(selector)
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta')
        document.head.appendChild(el)
      }
      if (selector.startsWith('meta[name="description"')) el.setAttribute('name', 'description')
      if (selector.startsWith('meta[name="robots"')) el.setAttribute('name', 'robots')
      if (selector.startsWith('link[rel="canonical"')) el.setAttribute('rel', 'canonical')
      el.setAttribute(attr, value)
    }
    upsert('meta[name="description"]', 'content', description)
    upsert('meta[name="robots"]', 'content', robots)
    upsert('link[rel="canonical"]', 'href', `https://prover.com.py${canonicalPath}`)
  }, [title, description, canonicalPath, robots])
}

function Shell({ eyebrow, title, children }) {
  return (
    <main style={base}>
      <div style={card}>
        <Link to="/" style={link}>← ProVer Clínica Oftalmológica</Link>
        <p style={{ marginTop: 34, letterSpacing: '.18em', textTransform: 'uppercase', color: '#c084fc', fontSize: 12, fontWeight: 800 }}>{eyebrow}</p>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 1.05, margin: '10px 0 26px' }}>{title}</h1>
        <div style={{ color: '#ddd4e8', lineHeight: 1.75, fontSize: 16 }}>{children}</div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 38, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,.1)' }}>
          <Link to="/privacidad" style={link}>Privacidad</Link>
          <Link to="/terminos" style={link}>Términos</Link>
          <Link to="/agenda" style={link}>Agendar consulta</Link>
        </div>
      </div>
    </main>
  )
}

export function ProverPrivacy() {
  useSeo('Política de Privacidad | ProVer', 'Cómo ProVer trata los datos enviados a través de sus canales digitales.', '/privacidad')
  return (
    <Shell eyebrow="Privacidad" title="Política de Privacidad">
      <p>ProVer utiliza los datos que una persona proporciona en sus canales digitales para gestionar consultas, solicitudes de contacto, agendamiento, verificación de identidad y las obligaciones operativas vinculadas con la atención.</p>
      <h2>Datos tratados</h2>
      <p>Según el servicio solicitado, pueden procesarse nombre, datos de contacto, cédula, fecha de nacimiento, información mínima de cobertura y registros técnicos indispensables para seguridad y auditoría. La web pública no debe utilizar datos de salud para publicidad comportamental.</p>
      <h2>Finalidades</h2>
      <p>Los datos se utilizan para coordinar la atención, verificar el número de WhatsApp, prevenir abuso, mantener trazabilidad y cumplir obligaciones operativas o legales aplicables.</p>
      <h2>Menores de edad</h2>
      <p>Cuando el paciente sea menor, el flujo digital puede requerir que un padre, madre, tutor o responsable autorizado confirme su vínculo y acepte el tratamiento de los datos necesarios para gestionar el turno.</p>
      <h2>Proveedores tecnológicos y enlaces externos</h2>
      <p>ProVer puede utilizar proveedores de infraestructura, mensajería, seguridad y soporte estrictamente para operar sus servicios digitales. QorIAX actúa como plataforma tecnológica de soporte y aplica separación por tenant, controles de acceso y trazabilidad. Al abrir servicios externos —por ejemplo mapas o WhatsApp— el proveedor externo puede recibir datos técnicos de conexión, como la dirección IP, conforme a sus propias políticas.</p>
      <h2>Conservación y seguridad</h2>
      <p>Los datos deben conservarse durante el período necesario para la finalidad correspondiente y las obligaciones aplicables. Se aplican controles de autenticación, autorización, verificación de WhatsApp, protección anti-abuso y auditoría.</p>
      <h2>Derechos y consultas</h2>
      <p>Las solicitudes de acceso, corrección, actualización u otras consultas sobre datos personales pueden canalizarse por los medios oficiales de ProVer. Cuando corresponda, se podrá requerir verificación de identidad.</p>
      <p><strong>Última actualización:</strong> 28 de septiembre de 2026.</p>
    </Shell>
  )
}

export function ProverTerms() {
  useSeo('Términos de Uso | ProVer', 'Condiciones para utilizar los servicios digitales públicos de ProVer.', '/terminos')
  return (
    <Shell eyebrow="Condiciones" title="Términos de Uso">
      <p>Los servicios digitales de ProVer facilitan información institucional, solicitudes de contacto y agendamiento. La disponibilidad mostrada puede cambiar hasta que el sistema confirme el turno.</p>
      <h2>Agendamiento</h2>
      <p>El usuario debe proporcionar información veraz y mantener acceso al número de WhatsApp utilizado para la verificación. Un turno sólo se considera registrado cuando la interfaz devuelve una confirmación exitosa.</p>
      <h2>Consentimiento e identidad</h2>
      <p>Antes de finalizar una reserva, el sistema puede requerir verificación de WhatsApp y aceptación expresa de la Política de Privacidad y de estos Términos.</p>
      <h2>Pacientes menores</h2>
      <p>Las reservas de menores deben ser gestionadas por un padre, madre, tutor o responsable autorizado. La confirmación digital del vínculo no sustituye otras autorizaciones que puedan requerirse para determinados procedimientos.</p>
      <h2>Uso responsable</h2>
      <p>No está permitido intentar acceder a información de terceros, automatizar solicitudes abusivas, falsear identidad, eludir controles de seguridad ni utilizar los canales para fines ilícitos.</p>
      <h2>Información médica</h2>
      <p>El contenido público de la web es informativo y no sustituye una evaluación profesional. Ante una urgencia, la persona debe utilizar los canales asistenciales apropiados y no depender de un formulario web.</p>
      <h2>Actualizaciones</h2>
      <p>Estas condiciones pueden actualizarse cuando cambien los servicios, requisitos de seguridad o normativa aplicable. La versión vigente se publica en esta página.</p>
      <p><strong>Última actualización:</strong> 28 de septiembre de 2026.</p>
    </Shell>
  )
}

export function ProverPortalSafe() {
  useSeo('Portal del Paciente | ProVer', 'Acceso al portal digital de ProVer.', '/portal', 'noindex, nofollow')
  return (
    <Shell eyebrow="Portal" title="Portal del paciente en actualización segura">
      <p>Estamos actualizando el acceso del portal para que la verificación de identidad y los mensajes de WhatsApp queden aislados específicamente para ProVer.</p>
      <p>Mientras se completa esa activación, el portal público no solicita códigos ni credenciales. Para gestionar un turno nuevo podés utilizar el agendamiento online.</p>
      <p><Link to="/agenda" style={link}>Ir a agendamiento →</Link></p>
    </Shell>
  )
}

export function ProverManagementSafe() {
  useSeo('Acceso interno | ProVer', 'Acceso interno de gestión de ProVer.', '/gestion', 'noindex, nofollow')
  return (
    <Shell eyebrow="Acceso interno" title="Gestión protegida">
      <p>El acceso de administración no expone cuentas demo ni credenciales públicas. La gestión interna se mantiene cerrada desde esta superficie pública durante la actualización del login tenant-aware.</p>
      <p>El personal autorizado debe utilizar el acceso operativo institucional ya asignado por QorIAX.</p>
    </Shell>
  )
}

export function ProverNotFound() {
  useSeo('Página no encontrada | ProVer', 'La página solicitada no existe.', window.location.pathname || '/', 'noindex, nofollow')
  return (
    <Shell eyebrow="404" title="Página no encontrada">
      <p>La dirección solicitada no corresponde a una página pública de ProVer.</p>
      <p><Link to="/" style={link}>Volver al inicio →</Link></p>
    </Shell>
  )
}
